import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { Plus, Pencil, Trash2, Settings, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const TABS = [
  { key: "rules", label: "Aturan Pinjaman" },
  { key: "loan_types", label: "Jenis Hutang" },
  { key: "borrower_types", label: "Jenis Peminjam" },
  { key: "tenors", label: "Tenor" },
];

const EMPTY_RULE = { loan_type_id: "", borrower_type_id: "", interest_rate: "", max_amount: "", max_tenor: "" };

export default function AdminMasterData() {
  const [tab, setTab] = useState("rules");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loanTypes, setLoanTypes] = useState([]);
  const [borrowerTypes, setBorrowerTypes] = useState([]);
  const [tenors, setTenors] = useState([]);
  const [rules, setRules] = useState([]);

  // modal aturan pinjaman
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/master-data");
      setLoanTypes(data?.loan_types || []);
      setBorrowerTypes(data?.borrower_types || []);
      setTenors(data?.tenors || []);
      setRules(data?.rules || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_RULE);
    setError("");
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      loan_type_id: r.loan_type_id ?? "",
      borrower_type_id: r.borrower_type_id ?? "",
      interest_rate: r.interest_rate ?? "",
      max_amount: r.max_amount ?? "",
      max_tenor: r.max_tenor ?? "",
    });
    setError("");
    setOpen(true);
  };

  const submitRule = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        loan_type_id: form.loan_type_id,
        borrower_type_id: form.borrower_type_id ? form.borrower_type_id : null,
        interest_rate: parseFloat(form.interest_rate) || 0,
        max_amount: parseFloat(form.max_amount) || 0,
        max_tenor: parseInt(form.max_tenor, 10) || 0,
      };
      if (editing) {
        await api.put(`/admin/loan-type-rules/${editing.id}`, payload);
        toast.success("Aturan pinjaman berhasil diperbarui");
      } else {
        await api.post("/admin/loan-type-rules", payload);
        toast.success("Aturan pinjaman berhasil ditambahkan");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_RULE);
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const deleteRule = async (r) => {
    if (!window.confirm(`Hapus aturan untuk ${r.loan_type_name || "-"} / ${r.borrower_type_name || "Semua"}?`)) return;
    setBusy(`rule-${r.id}`);
    try {
      await api.delete(`/admin/loan-type-rules/${r.id}`);
      toast.success("Aturan pinjaman dihapus");
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Master Data</h1>
            <p className="text-brand-muted text-sm mt-1">Kelola aturan pinjaman, jenis hutang, jenis peminjam, dan tenor.</p>
          </div>
          <button onClick={load} data-testid="master-data-reload-btn" className="rounded-full bg-brand-soft text-brand px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand/15 transition">
            <RefreshCcw size={14} /> Muat Ulang
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <FilterChip key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} testid={`master-tab-${t.key}`}>
              {t.label}
            </FilterChip>
          ))}
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3" data-testid="master-data-error">{error}</div>}

        {tab === "rules" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={openCreate} data-testid="open-rule-form-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
                <Plus size={14} /> Tambah Aturan
              </button>
            </div>
            <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rules-table">
                  <thead className="bg-brand-soft/60 text-left text-brand-muted">
                    <tr>
                      <th className="px-4 py-3">Jenis Pinjaman</th>
                      <th className="px-4 py-3">Jenis Peminjam</th>
                      <th className="px-4 py-3 text-right">Jasa %</th>
                      <th className="px-4 py-3 text-right">Maks Pinjaman</th>
                      <th className="px-4 py-3 text-right">Maks Tenor</th>
                      <th className="px-4 py-3 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && rules.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
                    : rules.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-center text-brand-muted">Belum ada aturan pinjaman</td></tr>
                    : rules.map((r) => (
                      <tr key={r.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                        <td className="px-4 py-3 font-medium text-brand-ink">{r.loan_type_name || "-"}</td>
                        <td className="px-4 py-3">{r.borrower_type_name || "Semua"}</td>
                        <td className="px-4 py-3 text-right">{r.interest_rate ?? 0}%</td>
                        <td className="px-4 py-3 text-right">{r.max_amount ?? 0}</td>
                        <td className="px-4 py-3 text-right">{r.max_tenor ?? 0} bln</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEdit(r)} data-testid={`edit-rule-${r.id}`} className="rounded-full bg-brand-soft text-brand px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-brand/15 transition">
                              <Pencil size={12} /> Edit
                            </button>
                            <button onClick={() => deleteRule(r)} disabled={busy === `rule-${r.id}`} data-testid={`delete-rule-${r.id}`} className="rounded-full bg-red-50 text-red-600 px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-red-100 transition disabled:opacity-60">
                              <Trash2 size={12} /> Hapus
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "loan_types" && (
          <LoanTypesTab
            items={loanTypes}
            loading={loading}
            busy={busy}
            setBusy={setBusy}
            onError={setError}
            reload={load}
          />
        )}

        {tab === "borrower_types" && (
          <BorrowerTypesTab
            items={borrowerTypes}
            loading={loading}
            busy={busy}
            setBusy={setBusy}
            onError={setError}
            reload={load}
          />
        )}

        {tab === "tenors" && (
          <TenorTab
            items={tenors}
            loading={loading}
            busy={busy}
            setBusy={setBusy}
            onError={setError}
            reload={load}
          />
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <form onSubmit={submitRule} className="bg-white rounded-3xl w-full sm:max-w-md p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="rule-form">
            <div className="font-heading font-bold text-lg text-brand-ink flex items-center gap-2">
              <Settings size={16} /> {editing ? "Edit Aturan Pinjaman" : "Tambah Aturan Pinjaman"}
            </div>
            <FormField label="Jenis Pinjaman">
              <select required value={form.loan_type_id} onChange={(e) => setForm((p) => ({ ...p, loan_type_id: e.target.value }))} className="inp" data-testid="rule-loan-type-select">
                <option value="">— Pilih jenis —</option>
                {loanTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
              </select>
            </FormField>
            <FormField label="Jenis Peminjam">
              <select value={form.borrower_type_id} onChange={(e) => setForm((p) => ({ ...p, borrower_type_id: e.target.value }))} className="inp" data-testid="rule-borrower-type-select">
                <option value="">Semua Jenis</option>
                {borrowerTypes.map((bt) => <option key={bt.id} value={bt.id}>{bt.name}</option>)}
              </select>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Jasa (%)">
                <input type="number" step="0.01" min="0" required className="inp" value={form.interest_rate} onChange={(e) => setForm((p) => ({ ...p, interest_rate: e.target.value }))} data-testid="rule-interest-input" />
              </FormField>
              <FormField label="Maks Pinjaman">
                <input type="number" min="0" required className="inp" value={form.max_amount} onChange={(e) => setForm((p) => ({ ...p, max_amount: e.target.value }))} data-testid="rule-max-amount-input" />
              </FormField>
            </div>
            <FormField label="Maks Tenor (bulan)">
              <input type="number" min="0" required className="inp" value={form.max_tenor} onChange={(e) => setForm((p) => ({ ...p, max_tenor: e.target.value }))} data-testid="rule-max-tenor-input" />
            </FormField>

            {error && <div className="rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2">{error}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" data-testid="rule-submit-btn" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function LoanTypesTab({ items, loading, busy, setBusy, onError, reload }) {
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/loan-types", { name: name.trim() });
      toast.success("Jenis hutang berhasil ditambahkan");
      setName("");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const edit = async (it) => {
    const next = window.prompt("Nama jenis hutang:", it.name);
    if (next === null) return;
    if (!next.trim()) { onError("Nama tidak boleh kosong"); return; }
    setBusy(`loan-${it.id}`);
    try {
      await api.put(`/admin/loan-types/${it.id}`, { name: next.trim() });
      toast.success("Jenis hutang berhasil diperbarui");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus jenis hutang "${it.name}"?`)) return;
    setBusy(`loan-${it.id}`);
    try {
      await api.delete(`/admin/loan-types/${it.id}`);
      toast.success("Jenis hutang dihapus");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="loan-type-form">
        <div className="flex-1 min-w-[200px]">
          <FormField label="Nama Jenis Hutang">
            <input className="inp" required value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. Pinjaman Barang" data-testid="loan-type-name-input" />
          </FormField>
        </div>
        <LoadingButton type="submit" loading={adding} loadingText="Menambah…" data-testid="loan-type-add-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:bg-brand-hover">
          <Plus size={14} /> Tambah
        </LoadingButton>
        <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
      </form>

      <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="loan-types-table">
            <thead className="bg-brand-soft/60 text-left text-brand-muted">
              <tr>
                <th className="px-4 py-3">Nama</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? <tr><td colSpan={2} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
              : items.length === 0 ? <tr><td colSpan={2} className="px-4 py-10 text-center text-brand-muted">Belum ada jenis hutang</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                  <td className="px-4 py-3 font-medium text-brand-ink">{it.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => edit(it)} disabled={busy === `loan-${it.id}`} data-testid={`edit-loan-type-${it.id}`} className="rounded-full bg-brand-soft text-brand px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-brand/15 transition disabled:opacity-60">
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => remove(it)} disabled={busy === `loan-${it.id}`} data-testid={`delete-loan-type-${it.id}`} className="rounded-full bg-red-50 text-red-600 px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-red-100 transition disabled:opacity-60">
                        <Trash2 size={12} /> Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BorrowerTypesTab({ items, loading, busy, setBusy, onError, reload }) {
  const [form, setForm] = useState({ name: "", installment_date: "25" });
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/borrower-types", { name: form.name.trim(), installment_date: parseInt(form.installment_date, 10) });
      toast.success("Jenis peminjam berhasil ditambahkan");
      setForm({ name: "", installment_date: "25" });
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const edit = async (it) => {
    const next = window.prompt("Nama jenis peminjam:", it.name);
    if (next === null) return;
    if (!next.trim()) { onError("Nama tidak boleh kosong"); return; }
    setBusy(`borrower-${it.id}`);
    try {
      await api.put(`/admin/borrower-types/${it.id}`, { name: next.trim(), installment_date: it.installment_date ?? 25 });
      toast.success("Jenis peminjam berhasil diperbarui");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus jenis peminjam "${it.name}"?`)) return;
    setBusy(`borrower-${it.id}`);
    try {
      await api.delete(`/admin/borrower-types/${it.id}`);
      toast.success("Jenis peminjam dihapus");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="borrower-type-form">
        <div className="flex-1 min-w-[200px]">
          <FormField label="Nama Jenis Peminjam">
            <input className="inp" required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="mis. Guru" data-testid="borrower-type-name-input" />
          </FormField>
        </div>
        <div className="w-40">
          <FormField label="Tanggal Angsuran">
            <select className="inp" value={form.installment_date} onChange={(e) => setForm((p) => ({ ...p, installment_date: e.target.value }))} data-testid="borrower-type-date-select">
              <option value="25">Tanggal 25</option>
              <option value="1">Tanggal 1</option>
            </select>
          </FormField>
        </div>
        <LoadingButton type="submit" loading={adding} loadingText="Menambah…" data-testid="borrower-type-add-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:bg-brand-hover">
          <Plus size={14} /> Tambah
        </LoadingButton>
        <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
      </form>

      <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="borrower-types-table">
            <thead className="bg-brand-soft/60 text-left text-brand-muted">
              <tr>
                <th className="px-4 py-3">Nama</th>
                <th className="px-4 py-3">Tanggal Angsuran</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? <tr><td colSpan={3} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
              : items.length === 0 ? <tr><td colSpan={3} className="px-4 py-10 text-center text-brand-muted">Belum ada jenis peminjam</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                  <td className="px-4 py-3 font-medium text-brand-ink">{it.name}</td>
                  <td className="px-4 py-3">Tanggal {it.installment_date ?? 25}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => edit(it)} disabled={busy === `borrower-${it.id}`} data-testid={`edit-borrower-type-${it.id}`} className="rounded-full bg-brand-soft text-brand px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-brand/15 transition disabled:opacity-60">
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => remove(it)} disabled={busy === `borrower-${it.id}`} data-testid={`delete-borrower-type-${it.id}`} className="rounded-full bg-red-50 text-red-600 px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-red-100 transition disabled:opacity-60">
                        <Trash2 size={12} /> Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TenorTab({ items, loading, busy, setBusy, onError, reload }) {
  const [months, setMonths] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!months) return;
    setAdding(true);
    try {
      await api.post("/admin/tenors", { months: parseInt(months, 10) });
      toast.success("Tenor berhasil ditambahkan");
      setMonths("");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus tenor ${it.months} bulan?`)) return;
    setBusy(`tenor-${it.id}`);
    try {
      await api.delete(`/admin/tenors/${it.id}`);
      toast.success("Tenor dihapus");
      reload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="bg-white rounded-3xl border border-brand/10 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="tenor-form">
        <div className="w-48">
          <FormField label="Tenor (bulan)">
            <input type="number" min="1" required className="inp" value={months} onChange={(e) => setMonths(e.target.value)} placeholder="mis. 12" data-testid="tenor-months-input" />
          </FormField>
        </div>
        <LoadingButton type="submit" loading={adding} loadingText="Menambah…" data-testid="tenor-add-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm hover:bg-brand-hover">
          <Plus size={14} /> Tambah
        </LoadingButton>
        <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
      </form>

      <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tenors-table">
            <thead className="bg-brand-soft/60 text-left text-brand-muted">
              <tr>
                <th className="px-4 py-3">Tenor</th>
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? <tr><td colSpan={2} className="px-4 py-10 text-center text-brand-muted">Memuat…</td></tr>
              : items.length === 0 ? <tr><td colSpan={2} className="px-4 py-10 text-center text-brand-muted">Belum ada tenor</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                  <td className="px-4 py-3 font-medium text-brand-ink">{it.months} bulan</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(it)} disabled={busy === `tenor-${it.id}`} data-testid={`delete-tenor-${it.id}`} className="rounded-full bg-red-50 text-red-600 px-3 py-1.5 text-xs inline-flex items-center gap-1 hover:bg-red-100 transition disabled:opacity-60">
                      <Trash2 size={12} /> Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={"rounded-full px-3 py-1.5 text-xs " + (active ? "bg-brand text-white" : "bg-white border border-brand/15 text-brand hover:bg-brand-soft")}>
      {children}
    </button>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}
